'use client'

import { createContext, useContext, useState } from 'react'
import Cookies from 'js-cookie'
import { updateConsent } from '../lib/gtm'
import { CONSENT_COOKIE_NAME, CONSENT_COOKIE_EXPIRES } from '../lib/consent'

type ConsentContextType = {
  consentGiven: boolean | null
  showBanner: boolean
  acceptConsent: () => void
  declineConsent: () => void
  resetConsent: () => void
}

type ConsentProviderProps = {
  children: React.ReactNode
  // Valeur lue dans le cookie côté serveur, null si aucun choix n'a été fait
  initialConsent: boolean | null
}

const ConsentContext = createContext<ConsentContextType | null>(null)

const DENIED = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  personalization_storage: 'denied',
} as const

const GRANTED = {
  analytics_storage: 'granted',
  ad_storage: 'granted',
  personalization_storage: 'granted',
} as const

export function ConsentProvider({ children, initialConsent }: ConsentProviderProps) {
  const [consentGiven, setConsentGiven] = useState<boolean | null>(initialConsent)
  const [showBanner, setShowBanner] = useState(initialConsent === null)

  const saveConsent = (analytics: boolean) => {
    const consent = { analytics, timestamp: Date.now() }
    Cookies.set(CONSENT_COOKIE_NAME, JSON.stringify(consent), {
      expires: CONSENT_COOKIE_EXPIRES,
    })
    setConsentGiven(analytics)
    setShowBanner(false)
    updateConsent(analytics ? GRANTED : DENIED)
  }

  const acceptConsent = () => saveConsent(true)

  const declineConsent = () => saveConsent(false)

  const resetConsent = () => {
    Cookies.remove(CONSENT_COOKIE_NAME)
    setConsentGiven(null)
    setShowBanner(true)
    updateConsent(DENIED)
  }

  return (
    <ConsentContext.Provider
      value={{ consentGiven, showBanner, acceptConsent, declineConsent, resetConsent }}
    >
      {children}
    </ConsentContext.Provider>
  )
}

export const useConsent = () => {
  const context = useContext(ConsentContext)
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider')
  }
  return context
}
